// Pure helpers behind the admin lesson-management UX:
// auto-generated lesson ids (no more hand-typed slugs) and drag-reorder
// arithmetic (no more hand-renumbering the `order` field).
// Kept SDK-free so they are trivially unit-testable.

let lastId = "";
let bump = 0;

/**
 * Generate a lesson doc id like `l-mbqz3k1x-4f2a`.
 * Time-prefixed so ids sort roughly by creation; random suffix avoids
 * collisions; `bump` disambiguates same-millisecond calls in one session.
 * Always matches /^[a-z0-9-]+$/ (the format the editor historically enforced).
 */
export function newLessonId(): string {
  let id = `l-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  if (id === lastId) id = `${id}${(++bump).toString(36)}`;
  else bump = 0;
  lastId = id;
  return id;
}

/** Return a copy of `list` with the item at `from` moved to `to` (both clamped). */
export function moveItem<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const clamp = (n: number) => Math.max(0, Math.min(list.length - 1, n));
  const [item] = next.splice(clamp(from), 1);
  next.splice(clamp(to), 0, item);
  return next;
}

/**
 * Given lessons in their desired display order, return the minimal set of
 * `{id, order}` writes that makes stored order = 1-based array position.
 * Also normalizes legacy duplicate/zero order values as a side effect.
 */
export function reorderWrites(lessons: { id: string; order: number }[]): { id: string; order: number }[] {
  return lessons
    .map((l, i) => ({ id: l.id, order: i + 1, changed: l.order !== i + 1 }))
    .filter((w) => w.changed)
    .map(({ id, order }) => ({ id, order }));
}

/**
 * Distinct non-empty section titles in first-appearance order.
 * Sections have no collection of their own - they exist implicitly as the
 * set of `sectionTitle` values across a course's lessons (pass them
 * order-sorted so the dropdown mirrors the curriculum). Titles are trimmed
 * so " Sprint 1" and "Sprint 1" collapse into one section.
 */
export function distinctSections(lessons: { sectionTitle?: string }[]): string[] {
  const seen = new Set<string>();
  for (const l of lessons) {
    const s = (l.sectionTitle ?? "").trim();
    if (s) seen.add(s);
  }
  return [...seen];
}

/**
 * Move a lesson and let it join the section it lands in, so the sidebar never
 * splits into duplicate sections. `section` forces a section (dropped on its header);
 * otherwise it takes the section of the lesson it passed.
 * Returns the new list and the moved lesson's new sectionTitle.
 */
export function placeLesson<T extends { sectionTitle?: string }>(
  list: T[],
  from: number,
  to: number,
  section?: string
): { next: T[]; sectionTitle: string } {
  const next = moveItem(list, from, to);
  const at = Math.max(0, Math.min(list.length - 1, to));
  const passed = to > from ? next[at - 1] : next[at + 1];
  const sectionTitle = (section ?? passed?.sectionTitle ?? list[from].sectionTitle ?? "").trim();
  next[at] = { ...next[at], sectionTitle };
  return { next, sectionTitle };
}
